/** @format */

import React, { FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { _$, Button, ButtonGroup, Datepicker, Message, Tooltip } from '@king-design/react'
import { ScheduleItemProps } from '../interface'
import { previewColorList, WEEKDAYS } from '../variables'
import { generateDutySchedule } from '../utils'

interface PreviewTableProps {
  className?: string
  formValue: ScheduleItemProps
}

const PreviewTable: FC<PreviewTableProps> = ({ className, formValue }) => {
  const [previewBtn, setPreviewBtn] = useState<string>('week')
  const [previewDate, setPreviewDate] = useState<[number, number]>([
    dayjs().startOf('day').valueOf(),
    dayjs().add(7, 'day').endOf('day').valueOf()
  ])
  const [now] = useState(() => dayjs().valueOf())
  // 修改时间范围同步button
  const onDateChange = value => {
    const [start, end] = value
    // 预览按“天”渲染，需统一到自然日边界，避免表头天数和时间轴比例不一致
    const startTs = dayjs(start).startOf('day').valueOf()
    const endTs = dayjs(end).endOf('day').valueOf()
    setPreviewDate([startTs, endTs])

    // 判断是否与 button 近1周、近2周、近1个月 的值一致
    const weekStart = dayjs().startOf('week').valueOf()
    const weekEnd = dayjs().endOf('week').valueOf()
    const twoWeekEnd = dayjs().endOf('week').add(1, 'week').valueOf()
    const monthStart = dayjs().startOf('month').valueOf()
    const monthEnd = dayjs().endOf('month').valueOf()

    const isSameDay = (a: number, b: number) =>
      dayjs(a).startOf('day').valueOf() === dayjs(b).startOf('day').valueOf()

    if (isSameDay(startTs, weekStart) && isSameDay(endTs, weekEnd)) {
      setPreviewBtn('week')
    } else if (isSameDay(startTs, weekStart) && isSameDay(endTs, twoWeekEnd)) {
      setPreviewBtn('twoweek')
    } else if (isSameDay(startTs, monthStart) && isSameDay(endTs, monthEnd)) {
      setPreviewBtn('month')
    } else {
      setPreviewBtn('')
    }
  }
  // 计算展示日期
  const headerDates = useMemo(() => {
    const [start, end] = previewDate
    let startDate = dayjs(start).startOf('day')
    const endDate = dayjs(end).endOf('day')
    const dates = []
    while (startDate.isBefore(endDate) || startDate.isSame(endDate, 'day')) {
      dates.push({
        date: startDate.format('YYYY-MM-DD'),
        dateLabel: startDate.format('MM-DD'),
        dayOfWeek: WEEKDAYS[startDate.day()]
      })
      startDate = startDate.add(1, 'day')
    }
    return dates
  }, [previewDate])

  // 生成值班轮转数据
  const scheduleData = useMemo(() => {
    const validShifts = formValue.DutyShift?.filter(
      item => Array.isArray(item) && item.length > 0
    ) as { ContactName: string; ContactID: number }[][]

    if (!validShifts?.length || !formValue.StartTime) return []

    return generateDutySchedule(
      validShifts,
      formValue.Period,
      formValue.ChangeTime,
      formValue.StartTime,
      formValue.EndTime || '',
      previewDate[0],
      previewDate[1],
      previewColorList
    )
  }, [
    formValue.DutyShift,
    formValue.Period,
    formValue.ChangeTime,
    formValue.StartTime,
    formValue.EndTime,
    previewDate
  ])

  // 日历视图：补齐前后空白格，按周换行
  const calendarCells = useMemo(() => {
    if (!headerDates.length) return []
    const firstDayOffset = dayjs(headerDates[0].date).day()
    const leading = Array.from({ length: firstDayOffset }, () => null)
    const trailingCount = (7 - ((leading.length + headerDates.length) % 7)) % 7
    const trailing = Array.from({ length: trailingCount }, () => null)
    return [...leading, ...headerDates, ...trailing]
  }, [headerDates])

  const scheduleMap = useMemo(() => {
    const map = new Map<string, typeof scheduleData[number]>()
    scheduleData.forEach(item => {
      map.set(item.date, item)
    })
    return map
  }, [scheduleData])

  const daySegmentMergeMap = useMemo(() => {
    const map = new Map<string, { mergeLeft: boolean[]; mergeRight: boolean[] }>()
    const ADJACENT_MS = 1000

    headerDates.forEach((day, index) => {
      const currentSegments = scheduleMap.get(day.date)?.segments || []
      const prevSegments =
        index > 0 ? scheduleMap.get(headerDates[index - 1].date)?.segments || [] : []
      const nextSegments =
        index < headerDates.length - 1
          ? scheduleMap.get(headerDates[index + 1].date)?.segments || []
          : []

      const mergeLeft = currentSegments.map((seg, segIndex) => {
        if (segIndex !== 0 || prevSegments.length === 0) return false
        const prevLast = prevSegments[prevSegments.length - 1]
        return (
          prevLast.shiftIndex === seg.shiftIndex &&
          seg.start >= prevLast.end &&
          seg.start - prevLast.end <= ADJACENT_MS
        )
      })

      const mergeRight = currentSegments.map((seg, segIndex) => {
        if (segIndex !== currentSegments.length - 1 || nextSegments.length === 0) return false
        const nextFirst = nextSegments[0]
        return (
          nextFirst.shiftIndex === seg.shiftIndex &&
          nextFirst.start >= seg.end &&
          nextFirst.start - seg.end <= ADJACENT_MS
        )
      })

      map.set(day.date, { mergeLeft, mergeRight })
    })

    return map
  }, [headerDates, scheduleMap])

  const segmentTooltipRangeMap = useMemo(() => {
    const map = new Map<string, { start: number; end: number }>()
    const ADJACENT_MS = 1000
    const flatSegments: Array<{
      key: string
      shiftIndex: number
      start: number
      end: number
    }> = []

    headerDates.forEach(day => {
      const segments = scheduleMap.get(day.date)?.segments || []
      segments.forEach((seg, segIndex) => {
        flatSegments.push({
          key: `${day.date}-${segIndex}-${seg.start}`,
          shiftIndex: seg.shiftIndex,
          start: seg.start,
          end: seg.end
        })
      })
    })

    flatSegments.sort((a, b) => a.start - b.start)

    let i = 0
    while (i < flatSegments.length) {
      const groupStart = flatSegments[i].start
      let groupEnd = flatSegments[i].end
      let j = i

      while (j + 1 < flatSegments.length) {
        const current = flatSegments[j]
        const next = flatSegments[j + 1]
        const isContinuous =
          current.shiftIndex === next.shiftIndex &&
          next.start >= current.end &&
          next.start - current.end <= ADJACENT_MS
        if (!isContinuous) break
        groupEnd = next.end
        j++
      }

      for (let k = i; k <= j; k++) {
        map.set(flatSegments[k].key, { start: groupStart, end: groupEnd })
      }
      i = j + 1
    }

    return map
  }, [headerDates, scheduleMap])

  const currentDayProgress = useMemo(() => {
    const start = dayjs(now).startOf('day').valueOf()
    const end = dayjs(now).endOf('day').valueOf()
    const ratio = ((now - start) / (end - start)) * 100
    return Math.max(0, Math.min(100, ratio))
  }, [now])

  if (headerDates.length === 0) return null

  return (
    <div className={className}>
      <div className="preview_card">
        <div className="preview_date">
          <ButtonGroup
            checkType="radio"
            value={previewBtn}
            onChangeValue={value => setPreviewBtn(value)}
          >
            <Button
              value="week"
              onClick={() =>
                setPreviewDate([
                  dayjs().startOf('day').valueOf(),
                  dayjs().add(7, 'day').endOf('day').valueOf()
                ])
              }
            >
              {_$('近1周')}
            </Button>
            <Button
              value="twoweek"
              onClick={() =>
                setPreviewDate([
                  dayjs().startOf('day').valueOf(),
                  dayjs().add(14, 'day').endOf('day').valueOf()
                ])
              }
            >
              {_$(`近{n}周`, { n: 2 })}
            </Button>
            <Button
              value="month"
              onClick={() =>
                setPreviewDate([
                  dayjs().startOf('day').valueOf(),
                  dayjs().add(30, 'day').endOf('day').valueOf()
                ])
              }
            >
              {_$('近30天')}
            </Button>
          </ButtonGroup>
          <Datepicker
            range
            value={previewDate}
            format="YYYY-MM-DD"
            onChangeValue={value => {
              const start = dayjs(value[0])
              const end = dayjs(value[1])
              if (end.diff(start, 'day') > 30) {
                Message.error(_$('结束时间不能超过开始时间30天'))
                return
              } else {
                onDateChange(value)
              }
            }}
          />
        </div>
      </div>
      <div className="duty_rotation_table">
        <div className="calendar_week_header">
          {WEEKDAYS.map(week => (
            <div key={week} className="calendar_week_title">
              {_$(week)}
            </div>
          ))}
        </div>
        <div className="calendar_grid">
          {calendarCells.map((cell, idx) => {
            if (!cell) return <div key={`empty-${idx}`} className="calendar_day_cell is-empty" />

            const daySchedule = scheduleMap.get(cell.date)
            const segments = daySchedule?.segments || []
            const isToday = dayjs(cell.date).isSame(now, 'day')
            const mergeInfo = daySegmentMergeMap.get(cell.date)
            const dayStart = dayjs(cell.date).startOf('day').valueOf()
            const dayEnd = dayjs(cell.date).endOf('day').valueOf()
            const dayTimelineItems: Array<
              | { type: 'gap'; start: number; end: number }
              | {
                  type: 'segment'
                  start: number
                  end: number
                  seg: typeof segments[number]
                  segIndex: number
                }
            > = []
            let cursor = dayStart

            segments.forEach((seg, segIndex) => {
              if (seg.end <= seg.start) return
              if (seg.start > cursor) {
                dayTimelineItems.push({ type: 'gap', start: cursor, end: seg.start })
              }
              dayTimelineItems.push({
                type: 'segment',
                start: seg.start,
                end: seg.end,
                seg,
                segIndex
              })
              cursor = Math.max(cursor, seg.end)
            })
            if (cursor < dayEnd) {
              dayTimelineItems.push({ type: 'gap', start: cursor, end: dayEnd })
            }

            return (
              <div key={cell.date} className={`calendar_day_cell ${isToday ? 'is-today' : ''}`}>
                {isToday && (
                  <div
                    className="calendar_today_time_line"
                    style={{ left: `calc(${currentDayProgress}% - 1px)` }}
                  />
                )}
                <div className="calendar_day_header">
                  <span className="date">{cell.dateLabel}</span>
                </div>
                <div className="calendar_day_content">
                  {segments.length ? (
                    <div className="calendar_day_track">
                      {dayTimelineItems.map((item, itemIndex) => {
                        const duration = Math.max(item.end - item.start, 1)

                        if (item.type === 'gap') {
                          return (
                            <div
                              key={`${cell.date}-gap-${itemIndex}-${item.start}`}
                              className="calendar_event_item"
                              style={{ flex: `${duration} ${duration} 0` }}
                            >
                              <div className="calendar_event_gap" />
                            </div>
                          )
                        }

                        const { seg, segIndex } = item
                        const mergedLeft = !!mergeInfo?.mergeLeft?.[segIndex]
                        const mergedRight = !!mergeInfo?.mergeRight?.[segIndex]
                        const segKey = `${cell.date}-${segIndex}-${seg.start}`
                        const tooltipRange = segmentTooltipRangeMap.get(segKey) || {
                          start: seg.start,
                          end: seg.end
                        }
                        return (
                          <div
                            key={segKey}
                            className="calendar_event_item"
                            style={{ flex: `${duration} ${duration} 0` }}
                          >
                            <Tooltip
                              slotContent={
                                <>
                                  <p
                                    style={{
                                      margin: '4px 0',
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-all',
                                      overflowWrap: 'anywhere'
                                    }}
                                  >
                                    {_$('值班班次')}: {seg.shiftName}
                                  </p>
                                  <p
                                    style={{
                                      margin: '4px 0'
                                    }}
                                  >
                                    {_$('开始时间')}:
                                    {dayjs(tooltipRange.start).format('YYYY-MM-DD HH:mm')}
                                  </p>
                                  <p
                                    style={{
                                      margin: '4px 0'
                                    }}
                                  >
                                    {_$('结束时间')}:
                                    {dayjs(tooltipRange.end).format('YYYY-MM-DD HH:mm')}
                                  </p>
                                </>
                              }
                            >
                              <div
                                className={`calendar_event ${mergedLeft ? 'is-merged-left' : ''} ${
                                  mergedRight ? 'is-merged-right' : ''
                                }`}
                                style={{ backgroundColor: seg.color }}
                              >
                                <span className="calendar_event_text">
                                  {mergedLeft ? (
                                    ''
                                  ) : (
                                    <>
                                      <i>#{seg.shiftIndex + 1}.</i> {seg.shiftName}
                                    </>
                                  )}
                                </span>
                              </div>
                            </Tooltip>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="calendar_day_empty">-</div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default styled(PreviewTable)`
  .preview_title {
    padding: 16px 0;
    margin-top: 16px;
    position: relative;
    font-size: 16px;
    vertical-align: middle;
    font-weight: 700;
    border-top: 1px solid #f0f0f0;
  }

  .preview_card {
    .preview_date {
      display: flex;
      align-items: center;
      & > * {
        margin-right: 8px;
      }
    }
  }

  .duty_rotation_table {
    margin-top: 16px;
    border: 1px solid #eee;
    border-radius: 4px;
    overflow: hidden;
    position: relative;
  }

  .calendar_week_header {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    background: #fafafa;
    border-bottom: 1px solid #eee;
  }

  .calendar_week_title {
    padding: 7px 6px;
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    color: #7b8495;
    border-right: 1px solid #f0f1f4;
    &:last-child {
      border-right: none;
    }
  }

  .calendar_grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }

  .calendar_day_cell {
    min-height: 62px;
    border-bottom: 1px solid #f0f1f4;
    border-right: 1px solid #f0f1f4;
    padding: 6px;
    background: #fff;
    position: relative;
    &:nth-child(7n) {
      border-right: none;
    }
    &.is-empty {
      background: #fafbfc;
    }
    &.is-today {
      .calendar_day_header .date {
        color: #f5222d;
        font-weight: 600;
        position: relative;
        display: inline-flex;
        align-items: center;
      }
      .calendar_day_header .date::before {
        content: '★';
        font-size: 10px;
        line-height: 1;
        margin-right: 4px;
        color: #f5222d;
      }
    }
  }

  .calendar_day_header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
    min-width: 0;
    .date {
      font-size: 12px;
      color: #4b5565;
      font-weight: 500;
    }
  }

  .calendar_today_time_line {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #f26674;
    pointer-events: none;
    z-index: 3;
  }

  .calendar_day_content {
    min-width: 0;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    height: 22px;
  }

  .calendar_day_track {
    width: 100%;
    min-width: 0;
    display: flex;
    border-radius: 4px;
    overflow: hidden;
  }

  .calendar_event_item {
    min-width: 0;
    display: flex;
  }

  .calendar_event_gap {
    width: 100%;
    height: 100%;
    background: transparent;
  }

  .calendar_event {
    width: 100%;
    padding: 2px 6px;
    color: #fff;
    font-size: 11px;
    line-height: 1.4;
    min-height: 22px;
    display: flex;
    align-items: center;
    overflow: hidden;
    opacity: 0.92;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.15);
    &.is-merged-left {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      padding-left: 2px;
    }
    &.is-merged-right {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      padding-right: 2px;
    }
  }

  .calendar_event_text {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    width: 100%;
  }

  .calendar_day_empty {
    color: #c0c4cc;
    font-size: 12px;
    width: 100%;
    text-align: center;
    line-height: 22px;
  }
`
